// i18n
import { translate, translateInComponent } from 'infras/i18n';
import locationTypeTranslations from 'infras/i18n/locales/location_type.json';

import Event from 'domains/models/event';
import { Role } from 'domains/models/event_type_user/role';
import { UserType } from 'domains/models/event_type_user/user_type';
import {
  EventTypeUserListItem,
  AdminEventTypeUser,
} from 'presentations/api/schema/event_type_user';
import {
  SharedEventTypeItem,
  EventTypeItem,
  EventTypeListItem,
} from 'presentations/api/schema/event_type';
import {
  EventTypeLocationItem,
  EventTypeLocationListItem,
} from 'presentations/api/schema/event_type_location';
import { formatToISO, getDateTime } from 'infras/datetime';
import EventType from 'domains/models/event_type';
import { ReservationMethod } from 'domains/models/event_type/reservationMethod';
import { SharedEventDetailItem } from 'presentations/api/schema/event';
import { WebhookEvent } from 'domains/models/webhook/webhookEvent';
import { EventTypeCalendarItem } from 'presentations/api/schema/event_type_calendar';
import { LocationType } from 'domains/models/event_type/locationType';
import { CampaignCode, addCampaignCode } from 'presentations/routes/helper';

export function splitHostAndUsersFromEventType(
  eventTypeUsers: EventTypeUserListItem[],
  reservationMethod?: ReservationMethod,
) {
  if (!eventTypeUsers) {
    return { host: null, users: [] };
  }
  let host = null;
  const users = [];
  for (const user of eventTypeUsers) {
    if (reservationMethod === ReservationMethod.Poll) {
      if (
        [UserType.Host, UserType.NotifyHost].includes(user.userType) &&
        user.role === Role.Admin
      ) {
        host = user;
      } else {
        users.push(user);
      }
    } else {
      if (user.userType === UserType.Host) {
        // TODO 複数ホスト
        if (host === null && user.role === Role.Admin) host = user;
        else users.push(user);
      }
    }
  }
  return { host, users };
}

export function splitHostAndUsersFromAdminEventTypeUsers(
  eventTypeUsers: AdminEventTypeUser[],
) {
  if (!eventTypeUsers) {
    return { host: null, users: [] };
  }
  if (eventTypeUsers.length === 1) {
    return { host: eventTypeUsers[0], users: [] };
  }
  let host = null;
  const users = [];
  for (const user of eventTypeUsers) {
    if (user.userType === UserType.Host) {
      if (user.role === Role.Admin) host = user;
      else users.push(user);
    }
  }
  return { host, users };
}

export function viewerOfEventType(
  eventType: EventTypeItem | EventTypeListItem,
  userId: string,
) {
  return eventType.users.find((u) => u.uid === userId);
}

export const getLocations = (
  location: EventTypeLocationItem | EventTypeLocationListItem | null,
) => {
  if (!location) return null;
  const locations = [];
  if (location.isInPerson) {
    locations.push({
      id: LocationType.InPerson,
      name: locationText(LocationType.InPerson),
      image: locationImage(LocationType.InPerson),
      text: location.address,
    });
  }
  if (location.isZoom) {
    locations.push({
      id: LocationType.Zoom,
      name: locationText(LocationType.Zoom),
      image: locationImage(LocationType.Zoom),
      text: null,
    });
  }
  if (location.isGoogle) {
    locations.push({
      id: LocationType.GoogleMeet,
      name: locationText(LocationType.GoogleMeet),
      image: locationImage(LocationType.GoogleMeet),
      text: null,
    });
  }
  if (location.isPhone) {
    locations.push({
      id: LocationType.Phone,
      name: locationText(LocationType.Phone),
      image: locationImage(LocationType.Phone),
      text: null,
    });
  }
  if (location.isAskInvitee) {
    locations.push({
      id: LocationType.AskInvitee,
      name: locationText(LocationType.AskInvitee),
      image: locationImage(LocationType.AskInvitee),
      text: null,
    });
  }
  if (location.isTeams) {
    locations.push({
      id: LocationType.Teams,
      name: locationText(LocationType.Teams),
      image: locationImage(LocationType.Teams),
      text: null,
    });
  }
  if (location.isCustom) {
    locations.push({
      id: LocationType.Custom,
      name: locationText(LocationType.Custom),
      image: locationImage(LocationType.Custom),
      text: location.customLocation,
    });
  }
  return locations;
};

export const formatLocationName = ({
  locationType,
  customLocation,
  address,
}: {
  locationType: LocationType;
  customLocation: string | null;
  address: string | null;
}) => {
  if (locationType === LocationType.Custom && customLocation) {
    return customLocation;
  }
  if (locationType === LocationType.InPerson && address) {
    return address;
  }
  return locationText(locationType);
};

export const locationText = (locationType: LocationType) => {
  const t = translateInComponent(locationTypeTranslations);
  return t(`locationType_${locationType}`);
};

export const locationImage = (locationType: LocationType) => {
  return `locations/${locationType}.svg`;
};

export const locationName = (
  location: EventTypeLocationItem | EventTypeLocationListItem | null,
) => {
  const locations = getLocations(location);
  const names = locations?.map((l) => l.name) || [];
  return names.join(translate('comma'));
};

export const addEventTypeRedirectProps = (
  webhookEvent: WebhookEvent,
  redirectUrl: string,
  eventType: EventType | SharedEventTypeItem,
  event: Event | SharedEventDetailItem,
  campaign?: CampaignCode,
) => {
  const url = new URL(redirectUrl);
  const tz = 'utc';
  const eventTz = event.timeZone || tz;
  url.searchParams.set('event', webhookEvent);
  url.searchParams.set('eventTypeUid', eventType.uid);
  url.searchParams.set('eventTypeName', eventType.name);
  url.searchParams.set('eventUid', event.uid);
  url.searchParams.set(
    'eventStartedAt',
    getDateTime(event.startedAt, tz).setZone(eventTz).toISO(),
  );
  url.searchParams.set(
    'eventEndedAt',
    getDateTime(event.endedAt, tz).setZone(eventTz).toISO(),
  );
  url.searchParams.set(
    'eventCreatedAt',
    getDateTime(event.createdAt, tz).setZone(eventTz).toISO(),
  );
  if (campaign) {
    return addCampaignCode(url.href, campaign);
  }
  return url.href;
};

interface Time {
  hour: number;
  minute: number;
}
export const getStartEndTime = (
  dateCalendarsList: EventTypeCalendarItem[][],
): { startTime: Time; endTime: Time } => {
  let startTime: Time | null = null;
  let endTime: Time | null = null;

  dateCalendarsList
    .flatMap((dateCalendars) => dateCalendars.flatMap((c) => c))
    .map((c) => {
      const calendarStartTime = c.startHour * 100 + c.startMinute;
      const calendarEndTime = c.endHour * 100 + c.endMinute;

      if (
        !startTime ||
        calendarStartTime < startTime.hour * 100 + startTime.minute
      ) {
        startTime = { hour: c.startHour, minute: c.startMinute };
      }
      if (!endTime || calendarEndTime > endTime.hour * 100 + endTime.minute) {
        endTime = { hour: c.endHour, minute: c.endMinute };
      }
    });

  return {
    startTime: startTime ?? { hour: 0, minute: 0 },
    endTime: endTime ?? { hour: 24, minute: 0 },
  };
};
